import type { MeDto } from '@posilka/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  acceptManyOrders,
  useAvailableOrders,
  type AvailableFilters,
} from '@posilka/client/api/driver';
import { MainAction } from '@/components/MainAction';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { haptic } from '@/lib/telegram';
import { useCities } from '@posilka/client/api/orders';
import { CitySelect } from '@/components/CitySelect';
import { OrderCard } from '@/components/OrderCard';
import { LinkRow } from '@/components/Section';
import { EmptyState, ErrorState, ListSkeleton } from '@/components/States';
import { addDays, todayTashkent } from '@posilka/client/lib/time';
import { DriverStatusCard } from './DriverStatusCard';
import { EarningsCard } from './EarningsCard';

export function DriverHome({ me }: { me: MeDto }) {
  const { t } = useTranslation();
  const approved = me.driverProfile?.status === 'APPROVED';
  const [filters, setFilters] = useState<AvailableFilters>({});
  const cities = useCities();
  const orders = useAvailableOrders(filters, approved);
  const items = orders.data?.pages.flatMap((p) => p.items) ?? [];
  const today = todayTashkent();
  const qc = useQueryClient();
  const errorMessage = useErrorMessage();
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);

  const toggle = (id: string) => {
    haptic.tap();
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };
  const acceptSelected = async () => {
    setBusy(true);
    setSummary(null);
    try {
      const r = await acceptManyOrders(selected);
      if (r.accepted.length) haptic.success();
      else haptic.error();
      const reasons = [...new Set(r.failed.map((f) => t(`errors.${f.code}`, f.code)))].join('; ');
      setSummary(
        t('driver.acceptManyResult', { accepted: r.accepted.length, failed: r.failed.length }) +
          (reasons ? ` — ${reasons}` : ''),
      );
      setSelected([]);
      setSelecting(false);
      void qc.invalidateQueries({ queryKey: ['driver'] });
    } catch (e) {
      haptic.error();
      setSummary(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!approved) return <DriverStatusCard profile={me.driverProfile} />;

  return (
    <>
      <EarningsCard />
      <div className="card mb-4 overflow-hidden">
        <LinkRow to="/driver/trips" icon="🗓">
          {t('trips.mine')}
          <span className="block text-xs font-medium text-muted">{t('trips.mineShort')}</span>
        </LinkRow>
      </div>
      <div className="mb-3 grid grid-cols-2 gap-2">
        <CitySelect
          cities={cities.data ?? []}
          value={filters.fromCityId ?? ''}
          placeholder={t('driver.filterFrom')}
          onChange={(fromCityId) =>
            setFilters((f) => ({ ...f, fromCityId: fromCityId || undefined }))
          }
        />
        <CitySelect
          cities={cities.data ?? []}
          value={filters.toCityId ?? ''}
          placeholder={t('driver.filterTo')}
          onChange={(toCityId) => setFilters((f) => ({ ...f, toCityId: toCityId || undefined }))}
        />
      </div>
      <div className="-mx-4 mb-4 flex gap-2 overflow-x-auto px-4 [scrollbar-width:none]">
        {[undefined, today, addDays(today, 1), addDays(today, 2)].map((date, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setFilters((f) => ({ ...f, date }))}
            className={`press shrink-0 rounded-full px-4 py-2 text-sm ${
              filters.date === date
                ? 'border border-lime bg-lime font-semibold text-lime-ink'
                : 'border border-line bg-surface'
            }`}
          >
            {date ? t(`order.day${i - 1}`) : t('driver.anyDate')}
          </button>
        ))}
        {(filters.fromCityId || filters.toCityId) && (
          <button
            type="button"
            className="shrink-0 px-2 text-sm text-link"
            onClick={() => setFilters({})}
          >
            ✕
          </button>
        )}
      </div>

      {items.length > 1 && (
        <div className="mb-3 flex justify-end">
          <button
            type="button"
            className="text-sm text-link"
            onClick={() => {
              setSelecting((s) => !s);
              setSelected([]);
            }}
          >
            {selecting ? t('common.cancel') : `☑️ ${t('driver.selectSeveral')}`}
          </button>
        </div>
      )}
      {summary && <p className="card mb-3 px-5 py-4 text-sm">{summary}</p>}

      {orders.isPending ? (
        <ListSkeleton />
      ) : orders.isError ? (
        <ErrorState error={orders.error} onRetry={() => void orders.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState icon="🔎" text={t('driver.noAvailable')} />
      ) : (
        <>
          {items.map((o) =>
            selecting ? (
              <div key={o.id} className="flex items-start gap-2">
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={selected.includes(o.id)}
                  aria-label={o.publicNumber}
                  onClick={() => toggle(o.id)}
                  className={`mt-6 grid h-7 w-7 shrink-0 place-items-center rounded-full border-2 ${
                    selected.includes(o.id)
                      ? 'border-lime bg-lime font-bold text-lime-ink'
                      : 'border-line'
                  }`}
                >
                  {selected.includes(o.id) ? '✓' : ''}
                </button>
                <div className="min-w-0 flex-1">
                  <OrderCard order={o} to={`/orders/${o.id}`} />
                </div>
              </div>
            ) : (
              <OrderCard key={o.id} order={o} to={`/orders/${o.id}`} />
            ),
          )}
          {orders.hasNextPage && (
            <button
              type="button"
              className="btn-secondary w-full"
              onClick={() => void orders.fetchNextPage()}
            >
              {t('common.loadMore')}
            </button>
          )}
        </>
      )}
      {selecting && (
        <MainAction
          text={t('driver.acceptSelected', { count: selected.length })}
          enabled={selected.length > 0 && selected.length <= 5}
          loading={busy}
          onClick={() => void acceptSelected()}
        />
      )}
    </>
  );
}
